import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js';
import {
  resizeShopifyImageUrl,
  computeSpatialOffset,
  computePlaneVisual,
  advancePlaneZ,
  advanceImageIndex,
  computeImageAdvance,
  parseGalleryData,
  getGalleryTier,
  shouldSkipWebGL,
  isEditableTarget,
  applyWheelDrift,
  applyKeyNudge,
  decayScrollForce,
} from './aaz-hero-gallery-helpers.js?v=4';

const DEPTH_RANGE = 50;
const AUTOPLAY_FORCE = 0.3;
const DRIFT_FACTOR = 0.003;
const DRIFT_MAX_FORCE = 4;
const DRIFT_DECAY_RATE = 0.985;
const SCROLL_LOCK_DISTANCE = 300;
const KEY_NUDGE_AMOUNT = 2;

const FADE_SETTINGS = {
  fadeIn: { start: 0.05, end: 0.25 },
  fadeOut: { start: 0.4, end: 0.43 },
};

const BLUR_SETTINGS = {
  blurIn: { start: 0, end: 0.1 },
  blurOut: { start: 0.4, end: 0.43 },
  maxBlur: 8,
};

const CURVE_VERTEX_CORE = `
  uniform float scrollForce;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vec3 pos = position;
    float curveIntensity = scrollForce * 0.3;
    float distanceFromCenter = length(pos.xy);
    float curve = distanceFromCenter * distanceFromCenter * curveIntensity;
    pos.z -= curve;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const CURVE_VERTEX_WITH_RIPPLE = `
  uniform float scrollForce;
  uniform float time;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vec3 pos = position;
    float curveIntensity = scrollForce * 0.3;
    float distanceFromCenter = length(pos.xy);
    float curve = distanceFromCenter * distanceFromCenter * curveIntensity;
    float ripple1 = sin(pos.x * 2.0 + scrollForce * 3.0) * 0.02;
    float ripple2 = sin(pos.y * 2.5 + scrollForce * 2.0) * 0.015;
    float clothEffect = (ripple1 + ripple2) * abs(curveIntensity) * 2.0;
    pos.z -= (curve + clothEffect);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const FRAGMENT_PLAIN = `
  uniform sampler2D map;
  uniform float opacity;
  varying vec2 vUv;
  void main() {
    vec4 color = texture2D(map, vUv);
    gl_FragColor = vec4(color.rgb, color.a * opacity);
  }
`;

const FRAGMENT_WITH_BLUR = `
  uniform sampler2D map;
  uniform float opacity;
  uniform float blurAmount;
  uniform vec2 texelSize;
  varying vec2 vUv;
  void main() {
    vec4 color = texture2D(map, vUv);
    if (blurAmount > 0.0) {
      vec4 blurred = vec4(0.0);
      float total = 0.0;
      for (float x = -2.0; x <= 2.0; x += 1.0) {
        for (float y = -2.0; y <= 2.0; y += 1.0) {
          vec2 offset = vec2(x, y) * texelSize * blurAmount;
          float weight = 1.0 / (1.0 + length(vec2(x, y)));
          blurred += texture2D(map, vUv + offset) * weight;
          total += weight;
        }
      }
      color = blurred / total;
    }
    gl_FragColor = vec4(color.rgb, color.a * opacity);
  }
`;

export function createGalleryMaterial(tier) {
  const uniforms = {
    map: { value: null },
    opacity: { value: 1 },
    scrollForce: { value: 0 },
    time: { value: 0 },
  };
  if (tier.useBlur) {
    uniforms.blurAmount = { value: 0 };
    uniforms.texelSize = { value: new THREE.Vector2(0, 0) };
  }
  return new THREE.ShaderMaterial({
    transparent: true,
    uniforms,
    vertexShader: tier.useRipple ? CURVE_VERTEX_WITH_RIPPLE : CURVE_VERTEX_CORE,
    fragmentShader: tier.useBlur ? FRAGMENT_WITH_BLUR : FRAGMENT_PLAIN,
  });
}

function createRenderer(canvas, tier) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, tier.maxPixelRatio));
  renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
  return renderer;
}

function createCamera(canvas) {
  const camera = new THREE.PerspectiveCamera(
    55,
    canvas.clientWidth / Math.max(canvas.clientHeight, 1),
    0.1,
    100
  );
  camera.position.set(0, 0, 0);
  return camera;
}

function loadTexture(loader, src) {
  return new Promise((resolve) => {
    loader.load(
      src,
      (texture) => resolve(texture),
      undefined,
      () => resolve(null)
    );
  });
}

async function loadGalleryTextures(images, tier) {
  const loader = new THREE.TextureLoader();
  loader.crossOrigin = 'anonymous';
  const textures = await Promise.all(
    images.map((img) => loadTexture(loader, resizeShopifyImageUrl(img.src, tier.textureWidth)))
  );
  return textures.filter((texture) => texture !== null);
}

function scaleForTexture(texture) {
  const aspect = texture.image ? texture.image.width / texture.image.height : 1;
  return aspect > 1 ? [2 * aspect, 2, 1] : [2, 2 / aspect, 1];
}

function createPlanes(scene, textures, tier, material) {
  const planes = [];
  for (let i = 0; i < tier.visibleCount; i++) {
    const texture = textures[i % textures.length];
    const planeMaterial = material.clone();
    planeMaterial.uniforms.map.value = texture;
    if (tier.useBlur) {
      planeMaterial.uniforms.texelSize.value.set(
        1 / texture.image.width,
        1 / texture.image.height
      );
    }
    const { x, y } = computeSpatialOffset(i);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1, 32, 32), planeMaterial);
    const [sx, sy] = scaleForTexture(texture);
    mesh.scale.set(sx, sy, 1);
    mesh.position.set(x, y, ((DEPTH_RANGE / tier.visibleCount) * i) % DEPTH_RANGE - DEPTH_RANGE / 2);
    scene.add(mesh);
    planes.push({
      mesh,
      material: planeMaterial,
      x,
      y,
      z: ((DEPTH_RANGE / tier.visibleCount) * i) % DEPTH_RANGE,
      imageIndex: i % textures.length,
    });
  }
  return planes;
}

function startAnimationLoop({ renderer, scene, camera, planes, textures, tier, driftState }) {
  const clock = new THREE.Clock();
  const imageAdvance = computeImageAdvance(tier.visibleCount, textures.length);
  let rafId = null;
  let onFirstFrame = null;

  function frame() {
    rafId = requestAnimationFrame(frame);
    const delta = Math.min(clock.getDelta(), 0.1);
    const elapsed = clock.getElapsedTime();

    let scrollForce = AUTOPLAY_FORCE;
    if (tier.enableScrollDrift) {
      driftState.force = decayScrollForce(driftState.force, AUTOPLAY_FORCE, DRIFT_DECAY_RATE);
      scrollForce = driftState.force;
    }

    for (const plane of planes) {
      const { z, wraps } = advancePlaneZ(plane.z, scrollForce, delta, DEPTH_RANGE);
      plane.z = z;
      if (wraps !== 0 && textures.length > 0) {
        plane.imageIndex = advanceImageIndex(plane.imageIndex, wraps, imageAdvance, textures.length);
        plane.material.uniforms.map.value = textures[plane.imageIndex];
        if (tier.useBlur) {
          const tex = textures[plane.imageIndex];
          plane.material.uniforms.texelSize.value.set(1 / tex.image.width, 1 / tex.image.height);
        }
      }

      plane.mesh.position.z = plane.z - DEPTH_RANGE / 2;

      const normalizedPosition = plane.z / DEPTH_RANGE;
      const { opacity, blur } = computePlaneVisual(
        normalizedPosition,
        FADE_SETTINGS,
        BLUR_SETTINGS,
        tier.useBlur
      );
      plane.material.uniforms.opacity.value = opacity;
      plane.material.uniforms.scrollForce.value = scrollForce;
      plane.material.uniforms.time.value = elapsed;
      if (tier.useBlur) {
        plane.material.uniforms.blurAmount.value = blur;
      }
    }

    renderer.render(scene, camera);

    if (onFirstFrame) {
      const cb = onFirstFrame;
      onFirstFrame = null;
      cb();
    }
  }

  return {
    start(onFirst) {
      onFirstFrame = onFirst || null;
      if (rafId === null) {
        clock.start();
        frame();
      }
    },
    stop() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    },
  };
}

function detectWebGLSupport() {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
  } catch {
    return false;
  }
}

function readGalleryData(sectionId) {
  const el = document.getElementById(`aaz-hero-gallery-data-${sectionId}`);
  if (!el) return [];
  return parseGalleryData(el.textContent);
}

async function initGallery(canvas) {
  const sectionId = canvas.dataset.sectionId;
  const heroEl = canvas.closest('.aaz-hero');
  const images = readGalleryData(sectionId);
  if (!heroEl || images.length === 0) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const webglSupported = detectWebGLSupport();

  if (shouldSkipWebGL({ prefersReducedMotion, saveData, webglSupported })) {
    return;
  }

  const tier = getGalleryTier(window.innerWidth);
  const renderer = createRenderer(canvas, tier);
  const camera = createCamera(canvas);
  const scene = new THREE.Scene();

  let stopped = false;
  let disposed = false;
  let loop = null;
  let observer = null;
  let planes = [];
  let textures = [];
  let resizeObserver = null;
  const driftState = { force: AUTOPLAY_FORCE };
  let isIntersecting = false;
  let scrollLockDistance = 0;

  function handleWheel(event) {
    if (!isIntersecting || document.visibilityState !== 'visible') return;
    if (event.deltaY > 0 && scrollLockDistance < SCROLL_LOCK_DISTANCE) {
      event.preventDefault();
      scrollLockDistance += event.deltaY;
    }
    driftState.force = applyWheelDrift(
      driftState.force,
      event.deltaY,
      DRIFT_FACTOR,
      -DRIFT_MAX_FORCE,
      DRIFT_MAX_FORCE
    );
  }

  function handleKeyDown(event) {
    if (!isIntersecting || document.visibilityState !== 'visible') return;
    if (isEditableTarget(event.target)) return;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      driftState.force = applyKeyNudge(driftState.force, -1, KEY_NUDGE_AMOUNT, -DRIFT_MAX_FORCE, DRIFT_MAX_FORCE);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      driftState.force = applyKeyNudge(driftState.force, 1, KEY_NUDGE_AMOUNT, -DRIFT_MAX_FORCE, DRIFT_MAX_FORCE);
    }
  }

  function cleanup() {
    if (disposed) return;
    disposed = true;
    stopped = true;
    if (loop) loop.stop();
    clearTimeout(resizeTimeout);
    canvas.removeEventListener('webglcontextlost', handleContextLost);
    if (resizeObserver) resizeObserver.disconnect();
    if (tier.enableScrollDrift) window.removeEventListener('wheel', handleWheel);
    if (tier.enableScrollDrift) document.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('visibilitychange', updatePlayState);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);
    if (observer) observer.disconnect();
    for (const plane of planes) {
      plane.mesh.geometry.dispose();
      plane.material.dispose();
    }
    for (const texture of textures) {
      texture.dispose();
    }
    renderer.dispose();
  }

  function handleContextLost(event) {
    event.preventDefault();
    heroEl.classList.remove('is-ready');
    cleanup();
  }
  canvas.addEventListener('webglcontextlost', handleContextLost);

  function handleSectionUnload(event) {
    if (event.detail && event.detail.sectionId === sectionId) cleanup();
  }
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  let resizeTimeout = null;
  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (width === 0 || height === 0) return;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }, 150);
  }

  function updatePlayState() {
    if (isIntersecting && document.visibilityState === 'visible') {
      loop.start();
    } else {
      loop.stop();
    }
  }

  textures = await loadGalleryTextures(images, tier);
  if (stopped || textures.length === 0) {
    cleanup();
    return;
  }

  const material = createGalleryMaterial(tier);
  planes = createPlanes(scene, textures, tier, material);
  loop = startAnimationLoop({ renderer, scene, camera, planes, textures, tier, driftState });

  resizeObserver = new ResizeObserver(handleResize);
  resizeObserver.observe(canvas);
  if (tier.enableScrollDrift) window.addEventListener('wheel', handleWheel, { passive: false });
  if (tier.enableScrollDrift) document.addEventListener('keydown', handleKeyDown);

  observer = new IntersectionObserver(
    (entries) => {
      isIntersecting = entries[0].isIntersecting;
      if (!isIntersecting) scrollLockDistance = 0;
      updatePlayState();
    },
    { threshold: 0.01 }
  );
  observer.observe(heroEl);

  document.addEventListener('visibilitychange', updatePlayState);

  loop.start(() => {
    heroEl.classList.add('is-ready');
  });
}

function initAllGalleries() {
  document.querySelectorAll('.aaz-hero__gallery-canvas').forEach((canvas) => {
    initGallery(canvas);
  });
}

function scheduleInit() {
  const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
  schedule(initAllGalleries);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInit);
} else {
  scheduleInit();
}
